import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { Loader2, TrendingUp, Activity, Target, Gauge, AlertTriangle, CheckCircle, BarChart3, Zap } from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, ComposedChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell
} from 'recharts';

function KPICard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="p-4 rounded-lg border transition-all hover:scale-[1.02]" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
      data-testid={`pccm-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</p>
          <p className="text-2xl font-bold mt-1" style={{ color: color || 'var(--text-primary)' }}>{value}</p>
          {sub && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</p>}
        </div>
        {Icon && <Icon className="w-5 h-5" style={{ color: color || 'var(--accent-primary)', opacity: 0.7 }} />}
      </div>
    </div>
  );
}

const PRIORITY_COLORS = { CRITICAL: '#ef4444', HIGH: '#f59e0b', MEDIUM: '#3b82f6', LOW: '#94a3b8' };

export default function PCCMAnalytics({ projectId }) {
  const { isDark } = useTheme();
  const [summary, setSummary] = useState(null);
  const [scurve, setScurve] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState('weekly');
  const [systemFilter, setSystemFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, curveRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/pccm/summary`),
        api.get(`/v1/projects/${projectId}/pccm/summary/s-curve?interval=${interval}${systemFilter ? `&system_code=${systemFilter}` : ''}`),
      ]);
      setSummary(sumRes.data);
      setScurve(curveRes.data?.sCurveData || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, interval, systemFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const gridColor = isDark ? '#1e293b' : '#e2e8f0';
  const tickColor = isDark ? '#64748b' : '#94a3b8';

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;
  if (!summary || summary.totalItems === 0) return <div className="text-center py-16 text-sm" style={{ color: 'var(--text-muted)' }}>No data. Add entries via the Register tab.</div>;

  const variance = (summary.overallActualProgress - summary.overallPlannedProgress).toFixed(1);
  const spi = summary.overallPlannedProgress > 0 ? (summary.overallActualProgress / summary.overallPlannedProgress).toFixed(3) : '1.000';

  const systemData = (summary.bySystem || []).map(s => ({
    name: s.systemCode, fullName: s.systemName, total: s.totalItems, completed: s.completedItems, overdue: s.overdueItems, progress: s.averageProgress,
  }));

  const sgData = (summary.stageGateBreakdown || []).map(sg => ({
    name: sg.stageGateId, fullName: sg.stageGateName, total: sg.total, completed: sg.completed, overdue: sg.overdue,
    completePct: sg.total > 0 ? Math.round(sg.completed / sg.total * 100) : 0,
  }));

  const priorityData = Object.entries(summary.byPriority || {}).filter(([, v]) => v > 0).map(([k, v]) => ({
    name: k, value: v, fill: PRIORITY_COLORS[k] || '#94a3b8',
  }));

  return (
    <div className="space-y-4" data-testid="pccm-analytics">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPICard label="Total Items" value={summary.totalItems} icon={Zap} color="#EF4444" />
        <KPICard label="Completed" value={summary.completed} sub={`${(summary.totalItems > 0 ? summary.completed / summary.totalItems * 100 : 0).toFixed(0)}%`} icon={CheckCircle} color="#22c55e" />
        <KPICard label="Overdue" value={summary.overdue} sub={summary.overdue > 0 ? 'Needs attention' : 'On track'} icon={AlertTriangle} color="#ef4444" />
        <KPICard label="Planned Progress" value={`${summary.overallPlannedProgress?.toFixed(1)}%`} icon={Target} color="#3b82f6" />
        <KPICard label="Actual Progress" value={`${summary.overallActualProgress?.toFixed(1)}%`} sub={`Variance: ${variance}%`} icon={Activity} color="#10b981" />
      </div>

      {/* S-Curve */}
      <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>S-Curve Progress</h4>
          <div className="flex items-center gap-2">
            <select value={systemFilter} onChange={e => setSystemFilter(e.target.value)} data-testid="analytics-system-filter"
              className="px-2 py-1 text-xs rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              <option value="">All Systems</option>
              {(summary.bySystem || []).map(s => <option key={s.systemCode} value={s.systemCode}>{s.systemCode}</option>)}
            </select>
            <div className="flex gap-1">
              {['weekly', 'monthly'].map(iv => (
                <button key={iv} onClick={() => setInterval(iv)} data-testid={`pccm-interval-${iv}`}
                  className="px-3 py-1 text-xs rounded-md transition-colors"
                  style={{
                    backgroundColor: interval === iv ? 'var(--accent-primary)' : 'var(--card-bg-alt)',
                    color: interval === iv ? '#fff' : 'var(--text-muted)',
                    border: interval === iv ? 'none' : '1px solid var(--border-color)',
                  }}>
                  {iv.charAt(0).toUpperCase() + iv.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
        {scurve.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={scurve}>
              <defs>
                <linearGradient id="pccmPlanGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} /><stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="pccmActualGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="periodLabel" tick={{ fontSize: 9, fill: tickColor }} interval="preserveStartEnd" angle={-45} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 10, fill: tickColor }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
              <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 11 }}
                formatter={(v, name) => [`${v?.toFixed(2)}%`, name]} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Area type="monotone" dataKey="plan" stroke="#3B82F6" fill="url(#pccmPlanGrad)" strokeWidth={2} name="Planned" />
              <Area type="monotone" dataKey="actual" stroke="#10B981" fill="url(#pccmActualGrad)" strokeWidth={2} name="Actual" />
              <Line type="monotone" dataKey="forecast" stroke="#F97316" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Forecast" />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-sm" style={{ color: 'var(--text-muted)' }}>No S-curve data available</div>
        )}
      </div>

      {/* Bottom Charts: System breakdown + Stage Gate funnel + Priority pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* System Breakdown */}
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Progress by System</h4>
          {systemData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={systemData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis type="number" tick={{ fontSize: 10, fill: tickColor }} domain={[0, 'dataMax']} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: tickColor }} width={50} />
                <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 11 }} />
                <Bar dataKey="completed" name="Completed" fill="#22c55e" radius={[0, 4, 4, 0]} />
                <Bar dataKey="overdue" name="Overdue" fill="#ef4444" radius={[0, 4, 4, 0]} />
                <Bar dataKey="total" name="Total" fill="#3b82f6" opacity={0.3} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No data</p>}
        </div>

        {/* Stage Gate Funnel */}
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Stage Gate Funnel</h4>
          <div className="space-y-2">
            {sgData.map(sg => (
              <div key={sg.name} className="p-2 rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{sg.fullName}</span>
                  <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{sg.completed}/{sg.total}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                    <div className="h-full rounded-full" style={{ width: `${sg.completePct}%`, background: '#EF4444' }} />
                  </div>
                  <span className="text-[10px] font-mono" style={{ color: sg.completePct > 50 ? '#22c55e' : '#ef4444' }}>{sg.completePct}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Priority Distribution */}
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Priority Distribution</h4>
          {priorityData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={priorityData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value">
                    {priorityData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2 justify-center">
                {priorityData.map(p => (
                  <span key={p.name} className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: p.fill }} />
                    {p.name}: {p.value}
                  </span>
                ))}
              </div>
            </>
          ) : <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No data</p>}
        </div>
      </div>
    </div>
  );
}
